import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import logo from '../../../assets/images/SVG/eduoneLogo.svg';
import AuthLeftPanel from '../../common/AuthLeftPanel';
import leftImage from '../../../assets/images/left-image-1.jpg';
import authService from '../../../infrastructure/services/authService';
import LoginOTPVerificationModal from './LoginOTPVerificationModal';
import AccountSelectionModal from './AccountSelectionModal';
import { getCookie, setCookie } from '../../../infrastructure/utils/cookieStorage';

const LoginPage = () => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        rememberMe: false
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const [showOTPVerification, setShowOTPVerification] = useState(false);
    const [verificationData, setVerificationData] = useState(null);
    const [showAccountSelection, setShowAccountSelection] = useState(false);
    const [availableAccounts, setAvailableAccounts] = useState([]);

    const navigate = useNavigate();

    // Redirect if already logged in
    useEffect(() => {
        const token = getCookie('authToken') || sessionStorage.getItem('authToken');
        if (token) {
            navigate('/dashboard');
        }
    }, [navigate]);

    // Load remembered credentials on mount
    useEffect(() => {
        try {
            const remembered = localStorage.getItem('student_remembered_credentials');
            if (remembered) {
                const credentials = JSON.parse(remembered);
                setFormData(prev => ({
                    ...prev,
                    email: credentials.email || '',
                    password: credentials.password || '',
                    rememberMe: true
                }));
            }
        } catch (e) {
            // ignore JSON parse errors
        }
    }, []);

    const formatIdentifier = (value) => {
        if (!value) return value;
        if (value.startsWith('07') && value.length === 10 && /^\d+$/.test(value)) {
            return '+94' + value.substring(1);
        }
        if (value.startsWith('7') && value.length === 9 && /^\d+$/.test(value)) {
            return '+94' + value;
        }
        return value;
    };

    const isValidEmail = (value) => {
        if (!value) return false;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^(?:\+94|0)?7\d{8}$/;
        return emailRegex.test(value) || phoneRegex.test(value);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let newValue = type === 'checkbox' ? checked : value;

        if (name === 'email' && typeof newValue === 'string') {
            newValue = formatIdentifier(newValue.trim());
        }

        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleLogin = async (e, selectedUserId = null) => {
        e?.preventDefault();
        const email = (formData.email || '').trim();
        const password = (formData.password || '').trim();

        const newErrors = {};
        if (!email) newErrors.email = 'Mobile, Email or ID is required';
        if (!password) newErrors.password = 'Password is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            setToast({ open: true, message: 'Please correct the errors before submitting', severity: 'error' });
            return;
        }

        setLoading(true);

        try {
            // Call the authentication service
            const content = await authService.login(email, password, selectedUserId);

            // Check if multi-account selection is required
            if (content?.requiresAccountSelection && content?.accounts) {
                setAvailableAccounts(content.accounts);
                setShowAccountSelection(true);
                setToast({ open: true, message: 'Multiple accounts found. Please select one.', severity: 'info' });
                return;
            }

            // Check if phone verification is required
            if (content?.requiresPhoneVerification) {
                setVerificationData({
                    userId: content.userId,
                    phoneNumber: content.phoneNumber,
                    email: content.email
                });
                setShowOTPVerification(true);
                setToast({ open: true, message: 'Phone number verification required. OTP sent to your phone/email.', severity: 'info' });
                return;
            }

            const { accessToken, user, studentRaw } = content;

            // Save auth data in encrypted cookies
            setCookie('authToken', accessToken);
            setCookie('student_auth_token', accessToken);
            setCookie('student_user', user);
            setCookie('studentId', studentRaw?.student_id || 'STU-2026-0084');

            // Handle credentials persistence
            if (formData.rememberMe) {
                localStorage.setItem('student_remembered_credentials', JSON.stringify({ email, password }));
            } else {
                localStorage.removeItem('student_remembered_credentials');
            }

            setToast({ open: true, message: 'Logged in successfully', severity: 'success' });

            // Navigate shortly after showing success toast
            setTimeout(() => {
                navigate('/dashboard');
            }, 600);
        } catch (err) {
            console.error(err);
            setToast({ open: true, message: err.message || 'Login failed. Please try again.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleAccountSelect = (account) => {
        setShowAccountSelection(false);
        // Retry login with selected userId
        handleLogin(null, account.id);
    };

    const handleOTPSuccess = () => {
        setShowOTPVerification(false);
        setToast({ open: true, message: 'Phone number verified successfully! Logging you in...', severity: 'success' });

        // Re-trigger login with the credentials now that phone verification is complete
        handleLogin(null, verificationData?.userId);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                height: { xs: 'auto', md: '100vh' },
                minHeight: '100vh',
                alignItems: 'stretch',
                padding: '24px',
                gap: '24px',
                background: '#F5F7F9',
                boxSizing: 'border-box',
                overflow: { xs: 'auto', md: 'hidden' }
            }}
        >
            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast((t) => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{
                    '& .MuiPaper-root': {
                        borderRadius: 12,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
                    }
                }}
            >
                <Alert
                    onClose={() => setToast((t) => ({ ...t, open: false }))}
                    severity={toast.severity}
                    variant="filled"
                    sx={{
                        width: '100%',
                        borderRadius: '12px',
                        ...(toast.severity === 'info' && {
                            bgcolor: '#ff6b35',
                            color: '#fff'
                        })
                    }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>

            <AuthLeftPanel image={leftImage} />

            <Box
                sx={{
                    flex: 1.1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: { xs: '20px', md: '24px 0 12px 0' },
                    boxSizing: 'border-box',
                    height: { xs: 'auto', md: '100%' },
                    overflowY: { xs: 'visible', md: 'auto' },
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': {
                        display: 'none'
                    }
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '480px',
                        my: 'auto'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mb: { xs: 3, md: 4 }
                        }}
                    >
                        <Box
                            component="img"
                            src={logo}
                            alt="FonixEdu"
                            sx={{
                                height: '45px',
                                width: 'auto'
                            }}
                        />
                    </Box>

                    <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
                        <Typography
                            variant="h1"
                            sx={{
                                fontSize: { xs: '26px', sm: '28px' },
                                fontWeight: 700,
                                color: '#231F20',
                                m: 0,
                                fontFamily: "'Poppins', sans-serif"
                            }}
                        >
                            Welcome Student!
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                color: '#9B9B9B',
                                lineHeight: 1.5,
                                mt: 1,
                                fontFamily: "'Poppins', sans-serif"
                            }}
                        >
                            Login to access your courses, live classes, assignments, and academic progress.
                        </Typography>
                    </Box>

                    <Box
                        component="form"
                        onSubmit={handleLogin}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: '1.5px solid',
                                    borderColor: errors.email ? '#e74c3c' : '#cbd5e1',
                                    borderRadius: '16px',
                                    backgroundColor: '#ffffff',
                                    transition: 'all 0.25s ease',
                                    '&:focus-within': {
                                        borderColor: '#ff502d',
                                        boxShadow: '0 0 0 3px rgba(255, 80, 45, 0.1)'
                                    }
                                }}
                            >
                                <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                        <polyline points="22,6 12,13 2,6"></polyline>
                                    </svg>
                                </Box>
                                <Box sx={{ width: '1.5px', height: '28px', backgroundColor: '#cbd5e1', alignSelf: 'center' }} />
                                <Box
                                    component="input"
                                    type="text"
                                    name="email"
                                    placeholder="Enter mobile, email, or ID"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    sx={{
                                        flex: 1,
                                        padding: '16px',
                                        border: 'none',
                                        background: 'transparent',
                                        fontSize: '15px',
                                        color: '#334155',
                                        outline: 'none',
                                        fontFamily: "'Poppins', sans-serif",
                                        width: '100%'
                                    }}
                                />
                                {formData.email && (
                                    <Box sx={{ px: 2, display: 'flex', alignItems: 'center' }}>
                                        {isValidEmail(formData.email) ? (
                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <circle cx="10" cy="10" r="10" fill="#2ecc71" />
                                                <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        ) : (
                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <circle cx="10" cy="10" r="10" fill="#e74c3c" />
                                                <path d="M6 6L14 14M14 6L6 14" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </Box>
                                )}
                            </Box>
                            {errors.email && (
                                <Typography component="span" sx={{ color: '#e74c3c', fontSize: '14px', mt: '4px', fontFamily: "'Poppins', sans-serif" }}>
                                    {errors.email}
                                </Typography>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: '1.5px solid',
                                    borderColor: errors.password ? '#e74c3c' : '#cbd5e1',
                                    borderRadius: '16px',
                                    backgroundColor: '#ffffff',
                                    transition: 'all 0.25s ease',
                                    '&:focus-within': {
                                        borderColor: '#ff502d',
                                        boxShadow: '0 0 0 3px rgba(255, 80, 45, 0.1)'
                                    }
                                }}
                            >
                                <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="2" y="6" width="20" height="12" rx="3" ry="3"></rect>
                                        <circle cx="7" cy="12" r="1.5" fill="#94a3b8"></circle>
                                        <circle cx="12" cy="12" r="1.5" fill="#94a3b8"></circle>
                                        <circle cx="17" cy="12" r="1.5" fill="#94a3b8"></circle>
                                    </svg>
                                </Box>
                                <Box sx={{ width: '1.5px', height: '28px', backgroundColor: '#cbd5e1', alignSelf: 'center' }} />
                                <Box
                                    component="input"
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    placeholder="Password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    sx={{
                                        flex: 1,
                                        padding: '16px',
                                        border: 'none',
                                        background: 'transparent',
                                        fontSize: '15px',
                                        color: '#334155',
                                        outline: 'none',
                                        fontFamily: "'Poppins', sans-serif",
                                        width: '100%'
                                    }}
                                />
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    sx={{
                                        px: 2,
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: '#94a3b8',
                                        transition: 'color 0.2s',
                                        '&:hover': {
                                            color: '#64748b'
                                        }
                                    }}
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {showPassword ? (
                                            <>
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                <circle cx="12" cy="12" r="3" />
                                            </>
                                        ) : (
                                            <>
                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                                <line x1="1" y1="1" x2="23" y2="23" />
                                            </>
                                        )}
                                    </svg>
                                </Box>
                            </Box>
                            {errors.password && (
                                <Typography component="span" sx={{ color: '#e74c3c', fontSize: '14px', mt: '4px', fontFamily: "'Poppins', sans-serif" }}>
                                    {errors.password}
                                </Typography>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
                            <Box
                                component="label"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: '#475569',
                                    fontFamily: "'Poppins', sans-serif",
                                    userSelect: 'none'
                                }}
                            >
                                <Box
                                    component="input"
                                    type="checkbox"
                                    name="rememberMe"
                                    checked={formData.rememberMe}
                                    onChange={handleInputChange}
                                    sx={{
                                        appearance: 'none',
                                        width: '20px',
                                        height: '20px',
                                        border: '1.5px solid #ffaa95',
                                        borderRadius: '6px',
                                        backgroundColor: '#fff',
                                        cursor: 'pointer',
                                        display: 'inline-grid',
                                        placeContent: 'center',
                                        marginRight: '10px',
                                        transition: 'all 0.2s ease',
                                        '&:checked': {
                                            backgroundColor: '#ff502d',
                                            borderColor: '#ff502d',
                                            '&::before': {
                                                content: '""',
                                                width: '10px',
                                                height: '6px',
                                                borderLeft: '2px solid white',
                                                borderBottom: '2px solid white',
                                                transform: 'rotate(-45deg) translate(1px, -1px)'
                                            }
                                        }
                                    }}
                                />
                                Remember me
                            </Box>

                            <Button
                                type="button"
                                onClick={() => navigate('/forgot-password')}
                                sx={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#ff502d',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    textTransform: 'none',
                                    p: 0,
                                    minWidth: 0,
                                    fontFamily: "'Poppins', sans-serif",
                                    fontWeight: 500,
                                    '&:hover': {
                                        color: '#e04324',
                                        background: 'none'
                                    }
                                }}
                            >
                                Forgot Password ?
                            </Button>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', mt: '10px' }}>
                            <Button
                                type="submit"
                                disabled={loading || !(formData.email || '').trim() || !(formData.password || '').trim()}
                                variant="contained"
                                sx={{
                                    width: '100%',
                                    background: '#ff502d',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '16px',
                                    py: 2,
                                    px: 3,
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    textTransform: 'none',
                                    boxShadow: '0 8px 20px -6px rgba(255, 80, 45, 0.4)',
                                    fontFamily: "'Poppins', sans-serif",
                                    transition: 'all 0.25s ease',
                                    '&:hover:not(:disabled)': {
                                        background: '#e04324',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 24px -6px rgba(255, 80, 45, 0.5)'
                                    },
                                    '&:disabled': {
                                        opacity: 0.6,
                                        backgroundColor: '#ff502d',
                                        color: '#ffffff',
                                        boxShadow: 'none',
                                        cursor: 'not-allowed'
                                    }
                                }}
                            >
                                {loading ? <CircularProgress size={20} color="inherit" /> : 'Login'}
                            </Button>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, my: { xs: 2.5, md: 3 }, width: '100%' }}>
                        <Box sx={{ flex: 1, height: '1.5px', backgroundColor: '#e2e8f0' }} />
                        <Typography sx={{ color: '#94a3b8', fontSize: '14px', whiteSpace: 'nowrap', fontFamily: "'Poppins', sans-serif" }}>
                            or sign up
                        </Typography>
                        <Box sx={{ flex: 1, height: '1.5px', backgroundColor: '#e2e8f0' }} />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2, gap: 1 }}>
                        <Typography sx={{ color: '#475569', fontSize: '15px', fontWeight: 400, m: 0, fontFamily: "'Poppins', sans-serif" }}>
                            Don't have an account ?
                        </Typography>
                        <Button
                            onClick={() => navigate('/register')}
                            sx={{
                                background: 'none',
                                border: 'none',
                                color: '#ff502d',
                                fontSize: '16px',
                                fontWeight: 600,
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                p: 0,
                                minWidth: 0,
                                textTransform: 'none',
                                fontFamily: "'Poppins', sans-serif",
                                '&:hover': {
                                    color: '#e04324',
                                    textDecoration: 'underline',
                                    background: 'none'
                                }
                            }}
                        >
                            Register
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ textAlign: 'center', mt: { xs: 2.5, md: 3.5 } }}>
                    <Typography sx={{ fontSize: '13px', color: '#94a3b8', m: 0, fontFamily: "'Poppins', sans-serif" }}>
                        By logging in, you agree to the EduOne.lk{' '}
                        <Box
                            component="a"
                            href="https://eduone.lk/terms-and-conditions"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                color: '#ff502d',
                                textDecoration: 'underline',
                                transition: 'color 0.2s ease',
                                fontWeight: 500,
                                '&:hover': {
                                    color: '#e04324'
                                }
                            }}
                        >
                            Terms & Conditions
                        </Box>
                        {' and '}
                        <Box
                            component="a"
                            href="https://eduone.lk/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                color: '#ff502d',
                                textDecoration: 'underline',
                                transition: 'color 0.2s ease',
                                fontWeight: 500,
                                '&:hover': {
                                    color: '#e04324'
                                }
                            }}
                        >
                            Privacy Policy.
                        </Box>
                    </Typography>
                </Box>
            </Box>

            <LoginOTPVerificationModal
                open={showOTPVerification}
                onClose={() => setShowOTPVerification(false)}
                verificationData={verificationData}
                onSuccess={handleOTPSuccess}
            />

            <AccountSelectionModal
                open={showAccountSelection}
                accounts={availableAccounts}
                onSelect={handleAccountSelect}
                onClose={() => setShowAccountSelection(false)}
            />
        </Box>
    );
};

export default LoginPage;